import { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectAuthRoutesList } from '@/pages/configuration/routeSlice';
import { getFilteredPermissionData } from '@/utils';
import { hasCapTableInstrumentImportAccess } from '@/utils/permission/capTableInstrumentImportPermission';
import type { NavMenuGroup } from '@packages/ui/layout';

// ---------------------------------------------------------------------------
// Config types
// ---------------------------------------------------------------------------

type PermissionRequirement = string | string[];

interface NavChildConfig {
  id: string;
  label: string;
  url: string;
  permissionKey?: PermissionRequirement;
  /** When set, nav item is shown only if user has any listed action on any listed module. */
  permissionModuleActions?: {
    modules: string[];
    actions: Array<'Create' | 'Edit'>;
  };
  /** When true, uses {@link hasCapTableInstrumentImportAccess} (Securities/Convertible/Warrants Create or Edit). */
  capTableInstrumentImport?: boolean;
}

interface NavGroupConfig {
  id: string;
  label: string;
  url?: string;
  children?: NavChildConfig[];
  /** ABP permission scope prefix used to filter children. */
  permissionScope?: string;
  mainTenantOnly?: boolean;
  /** Passed through to NavMenuGroup.permission for CheckPermissions wrapper. */
  viewPermission?: { type: 'View'; urlName: string };
  /** 'dropdown' renders children in a dropdown; 'spread' promotes each child to a top-level item. */
  childMode?: 'dropdown' | 'spread';
}

// ---------------------------------------------------------------------------
// Single source of truth for the entire customer nav structure
// ---------------------------------------------------------------------------

const NAV_CONFIG: NavGroupConfig[] = [
  { id: 'overview', label: 'Overview', url: '/overview' },

  {
    id: 'participants',
    label: 'Participants',
    url: '/participants',
    viewPermission: { type: 'View', urlName: 'participants' },
  },

  {
    id: 'awards-center',
    label: 'Awards Center',
    url: '/awards',
    permissionScope: 'CustomerService.Customer.',
    childMode: 'dropdown',
    children: [
      { id: 'rsu-awards', label: 'Restricted Stock', url: '/awards/rsu-awards', permissionKey: 'RSUAwards' },
      { id: 'option-awards', label: 'Option Awards', url: '/awards/option-awards', permissionKey: 'OptionAwards' },
      { id: 'psu-awards', label: 'PSU Awards', url: '/awards/psu-awards', permissionKey: 'PSUAwards' },
      { id: 'espp', label: 'ESPP', url: '/espp', permissionKey: 'OfferingPeriod' },
      { id: 'fair-value', label: 'Fair Value', url: '/awards/fair-value', permissionKey: ['RSUAwards', 'OptionAwards', 'PSUAwards'] },
      { id: 'unified-award-import', label: 'Unified Award Import', url: '/awards/unified-award-import', permissionKey: ['RSUAwards', 'OptionAwards', 'PSUAwards'] },
      { id: 'unified-awards', label: 'Unified Awards', url: '/awards/unified-awards', permissionKey: ['RSUAwards', 'OptionAwards', 'PSUAwards'] },
      { id: 'award-agreement', label: 'Award Agreement', url: '/awards/awards-agreement', permissionKey: ['RSUAwards', 'OptionAwards', 'PSUAwards'] },
      { id: 'vesting-tranches', label: 'Vesting Tranches', url: '/awards/vesting-tranches', permissionKey: ['RSUAwards', 'OptionAwards', 'PSUAwards'] },
    ],
  },

  {
    id: 'cap-table',
    label: 'Securities',
    url: '/cap-table',
    permissionScope: 'CustomerService.Customer.',
    childMode: 'dropdown',
    children: [
      { id: 'cap-table-summary', label: 'Cap Table', url: '/cap-table/summary', permissionKey: 'CapTableSummary' },
      { id: 'cap-table-financing-rounds', label: 'Financing Rounds', url: '/cap-table/financing-rounds', permissionKey: 'FundingRound' },
      { id: 'cap-table-exit-waterfall', label: 'Waterfall', url: '/cap-table/exit-waterfall', permissionKey: 'Waterfall' },
      { id: 'cap-table-securities', label: 'Shares', url: '/cap-table/securities', permissionKey: 'Securities' },
      { id: 'cap-table-convertibles', label: 'Convertibles', url: '/cap-table/convertibles', permissionKey: 'Convertible' },
      { id: 'cap-table-warrants', label: 'Warrants', url: '/cap-table/warrants', permissionKey: 'Warrants' },
      { id: 'cap-table-warrant-exercises', label: 'Exercise Requests', url: '/cap-table/warrant-exercises', permissionKey: 'WarrantExercise' },
      { id: 'cap-table-valuation', label: 'Valuations', url: '/cap-table/valuation', permissionKey: 'Valuation' },
      {
        id: 'cap-table-unified-instrument-import',
        label: 'Unified Import',
        url: '/cap-table/unified-instrument-import',
        capTableInstrumentImport: true,
      },
      {
        id: 'cap-table-ocf-import',
        label: 'OCF Import/Export',
        url: '/cap-table/ocf-import',
        permissionKey: 'OcfImport',
      },
    ],
  },

  {
    id: 'reports',
    label: 'Reports',
    url: '/reports',
    permissionScope: 'CustomerService.Customer.',
    childMode: 'spread',
    children: [
      { id: 'reports-main', label: 'Reports', url: '/reports', permissionKey: 'DynamicReport' },
    ],
  },

  {
    id: 'task-management',
    label: 'Task Management',
    permissionScope: 'CustomerService.Customer.',
    childMode: 'spread',
    children: [
      { id: 'tmc', label: 'Job Console', url: 'task-management-console', permissionKey: 'TaskManagerConsole' },
    ],
  },

  {
    id: 'customers',
    label: 'Customers',
    url: '/customer-list',
    mainTenantOnly: true,
    viewPermission: { type: 'View', urlName: 'customer-list' },
  },

  {
    id: 'transactions',
    label: 'Transactions',
    url: '/transactions',
    permissionScope: 'CustomerService.Customer.',
    childMode: 'dropdown',
    children: [
      { id: 'release', label: 'Release', url: '/transactions/release', permissionKey: 'Release' },
      { id: 'exercise', label: 'Exercise', url: '/transactions/exercise', permissionKey: 'OptionExercise' },
      { id: 'purchase', label: 'Purchase', url: '/transactions/purchase', permissionKey: 'Release' },
      { id: 'transfer-and-allocate', label: 'Transfer and Allocate', url: '/transactions/transfer-and-allocate', permissionKey: 'TransferAllocate' },
      { id: 'drs-registration-txn', label: 'DRS Registration', url: '/transactions/drs-registration', permissionKey: 'DrsRegistration' },
      { id: 'external-transaction-import', label: 'External Transaction Import', url: '/transactions/external-transaction-import', permissionKey: 'TransferAllocate' },
      { id: 'external-tax-import', label: 'External Tax Import', url: '/transactions/external-tax-import', permissionKey: 'TransactionTaxImport' },
      { id: 'inbound-share-transfer-import', label: 'Inbound Share Transfer Import', url: '/transactions/inbound-share-transfer-import', permissionKey: 'InboundShareTransfer' },
      { id: 'unified-import-export', label: 'Unified Transaction Import', url: '/transactions/unified-import-export', permissionKey: 'StockSplit' },
      { id: 'convertible-txn', label: 'Convertible', url: '/transactions/convertible', permissionKey: 'Convertible' },
    ],
  },

  {
    id: 'brokerage',
    label: 'Brokerage',
    url: '/dashboard',
    mainTenantOnly: true,
    viewPermission: { type: 'View', urlName: 'brokerage-import-export' },
    childMode: 'dropdown',
    children: [
      { id: 'brokerage-import', label: 'Import & Export', url: '/brokerage-import-export' },
      { id: 'brokerage-account', label: 'Brokerage Account', url: '/dashboard/brokerage-account' },
      { id: 'trade-order', label: 'Trade Order', url: '/dashboard/trade-order' },
      { id: 'transfer-allocate', label: 'Transfer and Allocate', url: '/dashboard/transfer-and-allocate' },
      { id: 'bank-account', label: 'Bank Account', url: '/dashboard/bank-account' },
      { id: 'cash-transfer', label: 'Cash Transfer', url: '/dashboard/cash-transfer' },
      { id: 'disbursement', label: 'Disbursement', url: '/dashboard/disbursement' },
    ],
  },

  {
    id: 'transfer-agent',
    label: 'Transfer Agent',
    url: '/transfer-agent',
    mainTenantOnly: true,
    viewPermission: { type: 'View', urlName: 'transfer-agent' },
    childMode: 'dropdown',
    children: [
      { id: 'drs-registration', label: 'DRS Registration', url: '/transfer-agent/drs-registration' },
    ],
  },

  { id: 'settings', label: 'Settings', url: '/settings' },
];

// ---------------------------------------------------------------------------
// Permission filter hook (replaces legacy NavPermissionCheck)
// ---------------------------------------------------------------------------

function usePermissionFilter(scope: string) {
  const grantedPolicies =
    useSelector(selectAuthRoutesList)?.auth?.grantedPolicies ?? {};
  const { nonFieldLevelPermissionsGroups } = getFilteredPermissionData(
    grantedPolicies,
    scope
  );
  const granted = useMemo(
    () => new Set<string>(Object.keys(nonFieldLevelPermissionsGroups ?? {})),
    [nonFieldLevelPermissionsGroups]
  );

  const checkPerm = useCallback(
    (key?: PermissionRequirement): boolean => {
      if (!key) return true;
      if (Array.isArray(key)) return key.some((k) => granted.has(k));
      return granted.has(key);
    },
    [granted]
  );

  const checkModuleActions = useCallback(
    (modules: string[], actions: Array<'Create' | 'Edit'>): boolean =>
      modules.some((module) =>
        (nonFieldLevelPermissionsGroups[module] as Array<{ text?: string }> | undefined)?.some(
          (entry) => entry.text != null && actions.includes(entry.text as 'Create' | 'Edit')
        )
      ),
    [nonFieldLevelPermissionsGroups]
  );

  const checkChildVisible = useCallback(
    (child: NavChildConfig): boolean => {
      if (child.capTableInstrumentImport) {
        return hasCapTableInstrumentImportAccess(grantedPolicies);
      }
      if (child.permissionModuleActions) {
        return checkModuleActions(
          child.permissionModuleActions.modules,
          child.permissionModuleActions.actions
        );
      }
      return checkPerm(child.permissionKey);
    },
    [checkModuleActions, checkPerm, grantedPolicies]
  );

  return { checkPerm, checkChildVisible };
}

// ---------------------------------------------------------------------------
// Public hook
// ---------------------------------------------------------------------------

interface UseNavMenuOptions {
  tenantType?: string | null;
}

/**
 * Builds the customer admin navigation menu from ABP permissions.
 * Returns NavMenuGroup[] that can be mapped directly to Navbar.* primitives.
 */
export function useNavMenu(options: UseNavMenuOptions = {}) {
  const { tenantType } = options;
  const hash = window.location.hash;
  const isMainTenant = tenantType === 'Main';
  const { checkChildVisible } = usePermissionFilter('CustomerService.Customer.');

  return useMemo(() => {
    const groups: NavMenuGroup[] = [];

    for (const cfg of NAV_CONFIG) {
      if (cfg.mainTenantOnly && !isMainTenant) continue;

      const filteredChildren = cfg.children
        ?.filter((c) => checkChildVisible(c))
        .map((c) => ({
          id: c.id,
          label: c.label,
          path: `${c.url}${hash}`,
          visible: true as const,
        }));

      if (cfg.childMode === 'spread') {
        for (const child of filteredChildren ?? []) {
          groups.push({ ...child, visible: true });
        }
      } else if (cfg.childMode === 'dropdown') {
        if (filteredChildren && filteredChildren.length > 0) {
          groups.push({
            id: cfg.id,
            label: cfg.label,
            path: `${cfg.url}${hash}`,
            children: filteredChildren,
            visible: true,
            permission: cfg.viewPermission,
          });
        }
      } else {
        groups.push({
          id: cfg.id,
          label: cfg.label,
          path: `${cfg.url}${hash}`,
          visible: true,
          permission: cfg.viewPermission,
        });
      }
    }

    return groups;
  }, [hash, isMainTenant, checkChildVisible]);
}
